
import { Box, Card, CardContent, CardHeader, Chip, Divider, LinearProgress, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TZGravatar from "../components/TZGravatar";
import { NoteAssociationType } from "../models/dtos";
import { GetUpsertIcon } from "../Helpers";
// Icons
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EventNoteIcon from '@mui/icons-material/EventNote';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NotesIcon from '@mui/icons-material/Notes';
import LaunchIcon from '@mui/icons-material/Launch';
// Popups
import { UpsertAction } from '../Enums';
import TalentManagementPopup from '../popups/TalentManagementPopup';
import NoteUpsertPopup from '../popups/NoteUpsertPopup';
import IncidentUpsertPopup from '../popups/IncidentUpsertPopup';
import PerformanceItemUpsertPopup from '../popups/PerformanceItemUpsertPopup';
import { DashboardCard } from '../apiClients/OverviewClient';
import useViewportMode from '../hooks/useViewportMode';
import { dashboardTileHeaderGlass } from '../theme/glass';
import { I18N } from '../I18N';

export interface DashboardTalentCardProps {
    card: DashboardCard;
    onRefresh?: () => void;
}

const DashboardTalentCard = ({ card, onRefresh }: DashboardTalentCardProps) => {
    const theme = useTheme();
    const { isPhone, isDesktop } = useViewportMode();
    const boundedCard = !isPhone;
    const touchActionSx = isDesktop ? undefined : { minWidth: 44, minHeight: 44 };
    const chipActionSx = isDesktop
        ? { padding: 0 }
        : { padding: 0, minWidth: 44, minHeight: 44 };
    const wrappingRowSx = { flexWrap: 'wrap', rowGap: 0.75, minWidth: 0 };
    const compactStatusRowSx = {
        minWidth: 0,
        maxWidth: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
        pl: 4,
        boxSizing: 'border-box',
    };
    const wrappingStatusChipSx = {
        minWidth: 0,
        maxWidth: '100%',
        height: 'auto !important',
        minHeight: 24,
        '& .MuiChip-label': {
            display: 'block',
            minWidth: 0,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            py: 0.25,
        },
    };
    const formatDate = (value?: string | null) => {
        if (!value) return "—";
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return "—";
        return parsed.toLocaleDateString();
    };

    const formatPeriod = (start?: string | null, end?: string | null) => {
        if (!start || !end) return "No active period";
        return `${formatDate(start)} – ${formatDate(end)}`;
    };

    const scoreDisplay = Number.isFinite(card.LastAssessmentScore)
        ? card.LastAssessmentScore.toFixed(1)
        : "—";

    const hasOpenIssues = card.OpenPerformanceItemCount > 0;
    const hasHighIssues = card.OpenPerformanceItemHighCount > 0;
    const hasMediumIssues = card.OpenPerformanceItemMediumCount > 0;
    const hasLowIssues = card.OpenPerformanceItemLowCount > 0;
    const currentAssessmentTotal = card.CurrentAssessmentTotalCount ?? 0;
    const currentAssessmentPercent = card.CurrentAssessmentPercentComplete ?? 0;
    const currentAssessmentScore = card.CurrentAssessmentScore ?? 0;
    const hasCurrentAssessment = currentAssessmentTotal > 0;
    const hasNotesInPeriod = card.NotesInPeriodCount > 0;
    const hasNotesTotal = card.NotesCount > 0;
    const positiveEvents = card.EventsPositiveCount ?? 0;
    const negativeEvents = card.EventsNegativeCount ?? 0;
    const hasEvents = positiveEvents + negativeEvents > 0;
    const hasPositiveEvents = positiveEvents > 0;
    const hasNegativeEvents = negativeEvents > 0;
    const hasActiveDevPlan = card.DevelopmentPlanActiveCount > 0;
    const hasCompletedDevPlan = card.DevelopmentPlanCompletedCount > 0;

    const developmentStatusColor = (status: string): "success" | "warning" | "default" => {
        switch (status) {
            case "On Track":
                return "success";
            case "Needs Items":
            case "Over Max":
                return "warning";
            default:
                return "default";
        }
    };

    const currentAssessmentScoreDisplay = Number.isFinite(currentAssessmentScore)
        ? currentAssessmentScore.toFixed(1)
        : "—";
    const currentAssessmentBarColor = currentAssessmentPercent >= 100
        ? theme.palette.success.main
        : "#f2a376";

    const splitChipLabel = (label: string, value: number | string) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>{label}</Box>
            <Box component="span" sx={{ width: 1, height: 12, bgcolor: 'currentColor', opacity: 0.5 }} />
            <Box component="span">{value}</Box>
        </Box>
    );

    return (
        <Box sx={{ minWidth: 0, maxWidth: '100%', height: boundedCard ? '100%' : 'auto' }}>
            <Card
                data-testid="dashboard-talent-card"
                data-viewport-mode={isPhone ? 'phone' : isDesktop ? 'desktop' : 'tablet'}
                sx={{ minWidth: 0, maxWidth: '100%', height: boundedCard ? '100%' : 'auto', display: 'flex', flexDirection: 'column' }}>
                <CardHeader
                    sx={{
                        textAlign: "left",
                        ...dashboardTileHeaderGlass(theme),
                        py: 0.75,
                        px: 1.5,
                        "& .MuiCardHeader-content": { minWidth: 0 },
                        "& .MuiCardHeader-subheader": {
                            color: "text.secondary",
                            opacity: 1,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                        }
                    }}
                    avatar={<TZGravatar email={card.EmailForAvatar} />}
                    title={
                        <Stack data-testid="dashboard-card-title-row" direction="row" alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5, minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                {card.FullName}
                            </Typography>
                        </Stack>
                    }
                    action={
                        <TalentManagementPopup
                            mode={UpsertAction.Update}
                            talentFullName={card.FullName}
                            talentId={card.TalentId}
                            icon={<DashboardCustomizeIcon />}
                            hoverContent="Open talent dashboard"
                            sx={touchActionSx}
                            onClose={onRefresh}
                        />
                    }
                    subheader={card.PositionTitle} />
                <CardContent
                    data-testid="dashboard-card-scroll-region"
                    sx={boundedCard ? {
                        flex: '1 1 auto',
                        minHeight: 0,
                        overflow: 'hidden',
                        px: 1.5,
                        py: 1.25,
                        '&:last-child': { pb: 1.25 },
                        '& .MuiChip-root': { height: 24 },
                    } : undefined}>
                    <Stack data-testid="dashboard-card-content-stack" spacing={0.75}>
                        <Stack data-testid="dashboard-review-period-row" direction={isPhone ? 'column' : 'row'} justifyContent="space-between" sx={wrappingRowSx}>
                            <Typography variant="caption" color="text.secondary">
                                Development Period
                            </Typography>
                            <Typography variant="body2">
                                {formatPeriod(card.ReviewPeriodStart, card.ReviewPeriodEnd)}
                            </Typography>
                        </Stack>
                        <Stack direction={isPhone ? 'column' : 'row'} justifyContent="space-between" sx={wrappingRowSx}>
                            <Typography variant="caption" color="text.secondary">
                                Last Development Record
                            </Typography>
                            <Typography variant="body2">
                                {formatDate(card.LastAssessmentDate)} • Score {scoreDisplay}
                            </Typography>
                        </Stack>
                        <Divider />
                        <Stack direction="row" alignItems="center" spacing={1} sx={wrappingRowSx}>
                            <AssessmentIcon fontSize="small" color={hasCurrentAssessment ? "info" : "disabled"} />
                            <Typography variant="body2">Development Progress</Typography>
                            {hasCurrentAssessment ? (
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flex={1}
                                    sx={!isDesktop ? { minWidth: isPhone ? '100%' : 240, flexWrap: 'wrap', rowGap: 1 } : undefined}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={currentAssessmentPercent}
                                        sx={{
                                            flex: 1,
                                            height: 8,
                                            borderRadius: 999,
                                            bgcolor: theme.palette.action.disabledBackground,
                                            "& .MuiLinearProgress-bar": {
                                                bgcolor: currentAssessmentBarColor
                                            }
                                        }}
                                    />
                                    <Typography variant="caption">{`${currentAssessmentPercent}%`}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {`Score ${currentAssessmentScoreDisplay}`}
                                    </Typography>
                                    <TalentManagementPopup
                                        mode={UpsertAction.Update}
                                        talentFullName={card.FullName}
                                        talentId={card.TalentId}
                                        initialTab="OngoingAssessmentEditor"
                                        hoverContent="Open current coaching session"
                                        onClose={onRefresh}
                                        sx={touchActionSx}
                                        icon={<LaunchIcon fontSize="small" />}
                                    />
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No active coaching session
                                </Typography>
                            )}
                        </Stack>
                        <Stack data-testid="dashboard-development-plan-row" spacing={0.5} sx={{ minWidth: 0, maxWidth: '100%' }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ minWidth: 0 }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                    <FactCheckOutlinedIcon fontSize="small" color={hasActiveDevPlan ? "info" : "disabled"} />
                                    <Typography variant="body2">Development Plan</Typography>
                                </Stack>
                                <TalentManagementPopup
                                    mode={UpsertAction.Update}
                                    talentFullName={card.FullName}
                                    talentId={card.TalentId}
                                    initialTab="DevelopmentPlan"
                                    hoverContent="Open development plan"
                                    onClose={onRefresh}
                                    sx={touchActionSx}
                                    icon={<LaunchIcon fontSize="small" />}
                                />
                            </Stack>
                            <Box data-testid="dashboard-development-statuses" sx={compactStatusRowSx}>
                                {hasActiveDevPlan && (
                                    <Chip size="small" color="info" label={splitChipLabel('Active', card.DevelopmentPlanActiveCount)} />
                                )}
                                {hasCompletedDevPlan && (
                                    <Chip size="small" label={splitChipLabel('Done', card.DevelopmentPlanCompletedCount)} />
                                )}
                                <Chip
                                    data-wrap-status="true"
                                    size="small"
                                    variant="outlined"
                                    color={developmentStatusColor(card.DevelopmentPlanStatus)}
                                    label={card.DevelopmentPlanStatus}
                                    sx={wrappingStatusChipSx}
                                />
                            </Box>
                        </Stack>
                        <Stack data-testid="dashboard-performance-items-row" spacing={0.5} sx={{ minWidth: 0, maxWidth: '100%' }}>
                            <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between" sx={{ minWidth: 0 }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                    <ReportProblemOutlinedIcon fontSize="small" color={hasOpenIssues ? "warning" : "disabled"} />
                                    <Typography variant="subtitle2">Performance Items</Typography>
                                </Stack>
                                {hasNegativeEvents && (
                                    <PerformanceItemUpsertPopup
                                        mode={UpsertAction.Create}
                                        inTalentId={card.TalentId}
                                        hoverContent="Add Performance Item"
                                        aria-label={I18N.Translate('Add Performance Item')}
                                        sx={touchActionSx}
                                        icon={GetUpsertIcon(UpsertAction.Create)}
                                        onClose={onRefresh}
                                    />
                                )}
                            </Stack>
                            <Box sx={compactStatusRowSx}>
                                {hasOpenIssues ? (
                                    <Stack direction="row" spacing={0.5} sx={wrappingRowSx}>
                                        {hasHighIssues && (
                                            <TalentManagementPopup
                                                mode={UpsertAction.Update}
                                                talentFullName={card.FullName}
                                                talentId={card.TalentId}
                                                initialTab="EventManagement"
                                                hoverContent="View performance items"
                                                size="small"
                                                sx={chipActionSx}
                                                disableRipple
                                                onClose={onRefresh}
                                                icon={<Chip size="small" color="error" label={splitChipLabel('High', card.OpenPerformanceItemHighCount)} />}
                                            />
                                        )}
                                        {hasMediumIssues && (
                                            <TalentManagementPopup
                                                mode={UpsertAction.Update}
                                                talentFullName={card.FullName}
                                                talentId={card.TalentId}
                                                initialTab="EventManagement"
                                                hoverContent="View performance items"
                                                size="small"
                                                sx={chipActionSx}
                                                disableRipple
                                                onClose={onRefresh}
                                                icon={<Chip size="small" color="warning" label={splitChipLabel('Med', card.OpenPerformanceItemMediumCount)} />}
                                            />
                                        )}
                                        {hasLowIssues && (
                                            <TalentManagementPopup
                                                mode={UpsertAction.Update}
                                                talentFullName={card.FullName}
                                                talentId={card.TalentId}
                                                initialTab="EventManagement"
                                                hoverContent="View performance items"
                                                size="small"
                                                sx={chipActionSx}
                                                disableRipple
                                                onClose={onRefresh}
                                                icon={<Chip size="small" label={splitChipLabel('Low', card.OpenPerformanceItemLowCount)} />}
                                            />
                                        )}
                                    </Stack>
                                ) : null}
                            </Box>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between" sx={wrappingRowSx}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={wrappingRowSx}>
                                <EventNoteIcon fontSize="small" color={hasEvents ? "info" : "disabled"} />
                                <Typography variant="body2">Events</Typography>
                                {hasPositiveEvents && (
                                    <TalentManagementPopup
                                        mode={UpsertAction.Update}
                                        talentFullName={card.FullName}
                                        talentId={card.TalentId}
                                        initialTab="EventManagement"
                                        hoverContent="Open events"
                                        size="small"
                                        sx={chipActionSx}
                                        disableRipple
                                        onClose={onRefresh}
                                        icon={<Chip size="small" color="success" icon={<ThumbUpAltOutlinedIcon fontSize="small" />} label={`${positiveEvents}`} />}
                                    />
                                )}
                                {hasNegativeEvents && (
                                    <PerformanceItemUpsertPopup
                                        mode={UpsertAction.Create}
                                        inTalentId={card.TalentId}
                                        hoverContent="Add Performance Item"
                                        aria-label={I18N.Translate('Add Performance Item')}
                                        size="small"
                                        sx={chipActionSx}
                                        disableRipple
                                        onClose={onRefresh}
                                        icon={<Chip size="small" color="error" icon={<WarningAmberIcon fontSize="small" />} label={`-${negativeEvents}`} />}
                                    />
                                )}
                                {!hasPositiveEvents && !hasNegativeEvents && (
                                    <Typography variant="body2" color="text.secondary">
                                        None
                                    </Typography>
                                )}
                            </Stack>
                            <IncidentUpsertPopup
                                mode={UpsertAction.Create}
                                inTalentId={card.TalentId}
                                hoverContent="Add Event"
                                aria-label={I18N.Translate('Add Event')}
                                onClose={onRefresh}
                                sx={touchActionSx}
                                icon={GetUpsertIcon(UpsertAction.Create)}
                            />
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between" sx={wrappingRowSx}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={wrappingRowSx}>
                            <NotesIcon fontSize="small" color={hasNotesInPeriod ? "primary" : "disabled"} />
                            <Typography variant="body2">Notes (period)</Typography>
                            {hasNotesInPeriod && (
                                <Chip size="small" color="primary" label={card.NotesInPeriodCount} />
                            )}
                            {hasNotesTotal ? (
                                <Typography variant="caption" color="text.secondary">
                                    Total {card.NotesCount}
                                </Typography>
                            ) : (
                                !hasNotesInPeriod && (
                                    <Typography variant="body2" color="text.secondary">
                                        None
                                    </Typography>
                                )
                            )}
                            </Stack>
                            <NoteUpsertPopup
                                title={`Add Note: ${card.FullName}`}
                                sx={isDesktop ? { alignSelf: 'end' } : { alignSelf: 'end', minWidth: 44, minHeight: 44 }}
                                mode={UpsertAction.Create}
                                associationId={card.TalentId}
                                associationType={NoteAssociationType.Talent}
                                hoverContent="Add Note"
                                visibleLabel={I18N.Translate('Add')}
                                aria-label={I18N.Translate('Add Note')}
                                onClose={onRefresh}
                                icon={GetUpsertIcon(UpsertAction.Create)}
                            />
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};

export default DashboardTalentCard;
